import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #mobileOpen = false;
  #mobileController;
  #headerObserver;
  #lifecycleController;
  #sectionHeight;
  #pageScroll = 0;

  #updateMobileBounds = () => {
    if (!this.#mobileOpen) return;
    if (window.innerWidth >= 990) { this.#closeMobile(false); return; }
    const header = this.closest('header-component');
    if (!header) return;
    const top = Math.max(0, header.getBoundingClientRect().bottom);
    const bottom = (window.visualViewport?.height ?? window.innerHeight) + (window.visualViewport?.offsetTop ?? 0);
    header.style.setProperty('--oudie-drawer-top', `${top}px`);
    header.style.setProperty('--oudie-drawer-height', `${Math.max(0, bottom - top)}px`);
  };

  #openMobile() {
    const header = this.closest('header-component');
    if (!header) return;
    this.#pageScroll = window.scrollY;
    window.scrollTo({ top: this.#pageScroll, behavior: 'instant' });
    const section = header.parentElement;
    this.#sectionHeight = section.style.height;
    section.style.height = `${section.getBoundingClientRect().height}px`;
    header.style.setProperty('--oudie-pinned-top', `${Math.max(0, header.getBoundingClientRect().top)}px`);
    this.#mobileOpen = true;
    this.refs.details.open = true;
    this.refs.details.classList.add('menu-open');
    const trigger = this.refs.details.querySelector('summary');
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('aria-label', this.dataset.closeLabel);
    header.setAttribute('data-oudie-menu-open', '');
    lockScroll(this.refs.details);
    removeTrapFocus();
    this.#mobileController?.abort();
    this.#mobileController = new AbortController();
    const options = { capture: true, signal: this.#mobileController.signal };
    const focusable = () => Array.from(header.querySelectorAll('summary, a[href], button:enabled, input:enabled, select:enabled, [tabindex="0"]')).filter(el => el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden' && !el.closest('[inert]'));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); this.#closeMobile(); return; }
      if (event.key !== 'Tab') return;
      const items = focusable();
      const first = items[0]; const last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }, options);
    document.addEventListener('focusin', event => {
      if (!header.contains(event.target)) trigger.focus({ preventScroll: true });
    }, options);
    header.addEventListener('click', event => {
      if (event.target instanceof Element && event.target.closest('search-button, header-actions, a[href]')) this.#closeMobile(false);
    }, options);
    window.addEventListener('scroll', this.#updateMobileBounds, options);
    this.#headerObserver = new ResizeObserver(this.#updateMobileBounds);
    this.#headerObserver.observe(header);
    this.#updateMobileBounds();
    trigger.focus({ preventScroll: true });
  }

  #closeMobile(restoreFocus = true) {
    if (!this.#mobileOpen) return;
    this.#mobileOpen = false;
    this.#mobileController?.abort();
    this.#headerObserver?.disconnect();
    const header = this.closest('header-component');
    header?.removeAttribute('data-oudie-menu-open');
    if (header) header.parentElement.style.height = this.#sectionHeight ?? '';
    this.querySelectorAll('details[open]').forEach(reset);
    reset(this.refs.details);
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    const trigger = this.refs.details.querySelector('summary');
    trigger.setAttribute('aria-label', this.dataset.openLabel);
    unlockScroll(this.refs.details);
    if (window.innerWidth < 990) window.scrollTo({ top: this.#pageScroll, behavior: 'instant' });
    removeTrapFocus();
    if (restoreFocus) trigger.focus({ preventScroll: true });
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#lifecycleController = new AbortController();
    const options = { signal: this.#lifecycleController.signal };
    window.addEventListener('resize', this.#updateMobileBounds, options);
    window.visualViewport?.addEventListener('resize', this.#updateMobileBounds, options);
    window.visualViewport?.addEventListener('scroll', this.#updateMobileBounds, options);
  }

  disconnectedCallback() {
    this.#closeMobile(false);
    this.#lifecycleController?.abort();
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    if (window.innerWidth < 990) event?.preventDefault();
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    if (details === this.refs.details && window.innerWidth < 990) { this.#openMobile(); return; }
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => { if (!this.#mobileOpen) trapFocus(details); }, { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    if (details === this.refs.details && this.#mobileOpen) { this.#closeMobile(); return; }
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          if (!this.#mobileOpen) trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
